import { useSelector } from 'react-redux';
import { selectBalanceDetails, selectPaymentState, selectResidentDetails } from '../sagas/residentpaymentsSlice';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import HelpIcon from '@mui/icons-material/Help';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { Box } from '@mui/system';
import { format } from 'date-fns';
import NumberFormat from 'react-number-format';
import MakePaymentModal, { ResPaymentType } from './MakePaymentModal';
import BalanceDetailsIcon from '../../../icons/BalanceDetailsIcon.svg';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

interface PropsType {
  handleFailure: () => void;
}

export default function CurrentBalanceTile(props: PropsType) {
  //local states
  const [openBalanceDetailsModal, setOpenBalanceDetailsModal] = React.useState(false);
  const [openMakePaymentModal, setOpenMakePaymentModal] = React.useState(false);

  //selectors
  const currentBalanceInfo = useSelector(selectBalanceDetails);
  const residentsDetails = useSelector(selectResidentDetails);
  const { isFetchingBalance, isBalanceFailed } = useSelector(selectPaymentState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetails, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  useEffect(() => {
    (isBalanceFailed || isFetchingPropertyGroupDetailsFailed) && props.handleFailure();
  }, [isBalanceFailed, isFetchingPropertyGroupDetailsFailed, props]);

  const showPendingPayment = !!currentBalanceInfo?.PendingPayment && +currentBalanceInfo.Balance !== 0;

  const handleClickPayNow = () => {
    setOpenBalanceDetailsModal(false);
    setOpenMakePaymentModal(true);
  };

  const handleOpenBalanceDetails = () => {
    setOpenBalanceDetailsModal(true);
    setOpenMakePaymentModal(true);
  };

  return (
    <>
      {isFetchingBalance || isFetchingPropertyGroupDetails ? (
        <Stack direction="column" justifyContent="center" alignItems="center" spacing={2}>
          <Typography id="balance">
            Current Balance
            <Tooltip
              title="Your balance is provided by your Property Manager. If there's a discrepancy only your Property Manager can resolve the issue. Pending payments are shown for display purposes only. The current balance will reflect these payments when your Property Manager updates their accounting software."
              placement="right"
            >
              <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
            </Tooltip>
          </Typography>
          <div style={{ marginTop: '50px' }}>
            <SpinnerWithNoConfirmation />
          </div>
        </Stack>
      ) : (
        <React.Fragment>
          <Typography id="balance">
            Current Balance
            <Tooltip
              title="Your balance is provided by your Property Manager. If there's a discrepancy only your Property Manager can resolve the issue. Pending payments are shown for display purposes only. The current balance will reflect these payments when your Property Manager updates their accounting software."
              placement="right"
            >
              <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
            </Tooltip>
          </Typography>
          <Typography id="lastUpdatedDate" marginBottom={showPendingPayment ? 1.5 : 3}>
            as of: {currentBalanceInfo && format(new Date(currentBalanceInfo.LeaseLastUpdatedAt), 'MM/dd/yyyy')}
          </Typography>
          <Typography id="balanceAmount" sx={{ marginBottom: showPendingPayment ? 0 : '5%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NumberFormat
              thousandsGroupStyle="thousand"
              value={(currentBalanceInfo.Balance ?? 0) / 100}
              prefix="$"
              decimalSeparator="."
              displayType="text"
              type="text"
              thousandSeparator={true}
              allowNegative={true}
              decimalScale={2}
              fixedDecimalScale={true}
            />
            <img src={BalanceDetailsIcon} alt="Show Balance Detail" style={{ cursor: 'pointer', marginLeft: '7px' }} onClick={handleOpenBalanceDetails} />
          </Typography>
          {showPendingPayment && (
            <>
              <Typography id="pendingPayment">
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(currentBalanceInfo.PendingPayment ?? 0) / 100}
                  prefix="-$"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
              {!!currentBalanceInfo?.Balance && +currentBalanceInfo.Balance !== 0 && (
                <Typography id="pendingPayment" marginBottom={2} sx={{ fontSize: '20px', lineHeight: '18px' }}>
                  Pending
                </Typography>
              )}
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignContent: 'end', marginTop: 'auto' }}>
            {currentBalanceInfo?.PaymentMethodAllowed && propertyGroupDetails.PaymentsEnabled && currentBalanceInfo.PropertyPaymentTypes?.length ? (
              <Button id="makeOtpBtn" variant="contained" onClick={handleClickPayNow}>
                Pay Now
              </Button>
            ) : (
              <Box>
                <Typography
                  style={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '22px',
                    color: '#000000',
                  }}
                >
                  {
                    !residentsDetails?.Lease.AcceptsPayments || (!residentsDetails?.AchEnabled && !residentsDetails?.CreditcardEnabled && !residentsDetails?.DebitcardEnabled)
                      ? 'Please contact your Property Manager to request the ability to pay.'
                      : !currentBalanceInfo.PropertyPaymentTypes?.length
                      ? 'We are unable to accept a payment at this time. Please contact support for assistance.'
                      : 'Payments are not currently accepted' //!iPhone sometimes has this onload. Doesn't go away.
                  }
                </Typography>
              </Box>
            )}
          </Box>

          {openMakePaymentModal && (
            <MakePaymentModal
              open={openMakePaymentModal}
              residentsDetails={residentsDetails}
              currentTab={ResPaymentType.OTP}
              closeCallback={() => setOpenMakePaymentModal(false)}
              openBalanceDetailsModal={openBalanceDetailsModal}
            />
          )}
        </React.Fragment>
      )}
    </>
  );
}
